import { ReactElement, useMemo } from 'react';
import Certificate, { CertificateI } from './certificate';
import { Container, FooterContainer, Label, ResultContainer } from './styles';
import { ReactComponent as MainLogoIcon } from '@assets/images/logo.svg';
import Lang from '@lang/en';
import dayjs from 'dayjs';
import {
  dateFriendlyFormat,
  dateTime24FriendlyFormatWithComma,
} from '@app/utils';
import { useParams } from 'react-router-dom';
import useResultData from './useResultData';
import Loader from '@app/common/loadData/Loader';
import CertificateNotFound from './certificateNotFound';
import { ResultI } from './types';
import { getTestTypeLabel } from '../ordersTable/utils';

export type FieldI = {
  label: string;
  value: string;
};

export const ResultComponent = ({
  values,
}: {
  values: CertificateI;
}): ReactElement => {
  return (
    <ResultContainer>
      <Container>
        <Certificate values={values} />
        <FooterContainer>
          <Label>{Lang.healthCheckResultCertificateProvidedByText}</Label>
          <MainLogoIcon />
        </FooterContainer>
      </Container>
    </ResultContainer>
  );
};

const Result = (): ReactElement => {
  const params = useParams<{ token: string }>();
  const token = params.token;
  const { resultData: certificateData, loading, error } = useResultData(token);

  const values = useMemo(() => {
    return mapValuesToDisplay(certificateData);
  }, [certificateData]);

  if (error) {
    return <CertificateNotFound />;
  }

  return loading ? <Loader /> : <ResultComponent values={values} />;
};

export default Result;

export const mapValuesToDisplay = (certificateData: ResultI): CertificateI => {
  const { result } = certificateData;
  return {
    patientName: `${certificateData.firstName} ${certificateData.lastName}`,
    result: result ? `${result[0].toUpperCase()}${result.slice(1)}` : '',
    birthdate: dayjs(certificateData.birthdate).format(dateFriendlyFormat),
    passportNumber: certificateData.passportNumber,
    arrivalDate: dayjs(certificateData.arrivalDate).format(dateFriendlyFormat),
    sampleType: certificateData.sampleType,
    timeSampled: dayjs(certificateData.sampleTakenAt).format(
      dateTime24FriendlyFormatWithComma
    ),
    testLabel: getTestTypeLabel(
      certificateData.testType,
      certificateData.testCategory
    ),
    timeAnalysed: dayjs(certificateData.analysedAt).format(
      dateTime24FriendlyFormatWithComma
    ),
  };
};
